import { FC, useEffect } from "react";

import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import MealInsightsCubit from "src/state/MealInsightsCubit/MealInsightsCubit";
import MealsStatsCubit from "src/state/MealsStatsCubit/MealsStatsCubit";
import { LoadingIndicator } from "src/ui/components/Loader/Loader";

import MealTodayEmptyCard from "./MealTodayEmptyCard";
import MealTodayInsightsCard from "./MealTodayInsightsCard";
import MealTodayStats from "./MealTodayStats";
import RxDietCard from "./RxDietCard";

const Overview = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
`;

const EmptyStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const LoadingWrap = styled.div`
  position: relative;
  min-height: 220px;
`;

interface MealTodayOverviewProps {
  /**
   * Opens the meal photo picker, shared with the page's FAB. Omitted on desktop,
   * where meal upload isn't offered.
   */
  onAddMeal?: () => void;
  /** Forwarded to the empty card, which shows a QR to the app on desktop. */
  variant?: "mobile" | "desktop";
}

const MealTodayOverview: FC<MealTodayOverviewProps> = ({
  onAddMeal,
  variant = "mobile"
}) => {
  const [
    {
      todaySummary,
      initialLoadComplete,
      loading: historyLoading,
      error: historyError
    }
  ] = useBlocNext(MealInsightsCubit);
  const [
    { loaded, insights, loading: statsLoading, error: statsError },
    { load }
  ] = useBlocNext(MealsStatsCubit);

  useEffect(() => {
    if (!loaded) {
      void load();
    }
  }, [loaded, load]);

  // After a meal is saved both cubits are invalidated and refetch. Until they
  // settle, todaySummary/insights still hold the stale pre-upload values, so
  // rendering now would briefly flash the "no meal today" card before the real
  // content appears. Hold a spinner until the refreshed data lands — falling
  // through on error so a failed load doesn't spin forever.
  const historyPending =
    (!initialLoadComplete || historyLoading) && !historyError;
  const statsPending = (!loaded || statsLoading) && !statsError;

  if (historyPending || statsPending) {
    return (
      <LoadingWrap>
        <LoadingIndicator />
      </LoadingWrap>
    );
  }

  // Nothing logged today (but meals exist on earlier days): prompt the member to
  // start the day rather than showing empty rings/stats.
  if (todaySummary.mealCount === 0) {
    return (
      <EmptyStack>
        <MealTodayEmptyCard onAddMeal={onAddMeal} variant={variant} />
        <RxDietCard />
      </EmptyStack>
    );
  }

  // The backend decides when there's an insight worth showing (it returns none
  // if the AI summary can't be generated), so we simply follow whether it sent
  // one rather than gating on a local meal-count heuristic.
  const hasInsight = Boolean(insights);

  return (
    <Overview>
      {hasInsight && <MealTodayInsightsCard />}
      <MealTodayStats withCard={hasInsight} />
    </Overview>
  );
};

export default MealTodayOverview;
